import { useAuthoringServices } from "./services";
import { type Project, type OutputProfile, type Imposition } from "@proof/core";
import { useDocumentScene } from "@proof/ui";
import { Control, Toggle } from "./components";
import { InsetsControl } from "./InsetsControl";
import { SheetStockEditor } from "./SheetStockEditor";
const emptyValues = {};
export function SheetSettings({
  project,
  output,
  onChange,
}: {
  project: Project;
  output: OutputProfile;
  onChange: (config: Imposition) => void;
}) {
  const services = useAuthoringServices();
  const config = output.imposition,
    update = (patch: Partial<Imposition>) => onChange({ ...config, ...patch }),
    proof = useDocumentScene(
      project,
      emptyValues,
      undefined,
      undefined,
      output,
      services.loadFont,
    ),
    plan = proof.scene?.imposition;
  return (
    <>
      <Toggle
        label="Arrange copies on a sheet"
        value={config.enabled}
        onChange={(enabled) => update({ enabled })}
      />
      {config.enabled && (
        <>
          <Control label="Standard sheet size">
            <select
              value=""
              onChange={(e) => {
                if (!e.target.value) return;
                const [sheetWidth, sheetHeight] = e.target.value
                  .split(",")
                  .map(Number);
                update({ sheetWidth, sheetHeight });
              }}
            >
              <option value="">Custom / choose a standard size</option>
              <option value="612,792">Letter - 8.5 x 11 in</option>
              <option value="792,1224">Tabloid - 11 x 17 in</option>
              <option value="936,1368">Digital press - 13 x 19 in</option>
              <option value="595.275591,841.889764">A4 - 210 x 297 mm</option>
              <option value="907.086614,1275.590551">
                SRA3 - 320 x 450 mm
              </option>
            </select>
          </Control>
          <div className="control-grid">
            {(["sheetWidth", "sheetHeight", "copies"] as const).map((key) => (
              <Control
                key={key}
                label={
                  key === "copies"
                    ? "Copies"
                    : key === "sheetWidth"
                      ? "Sheet width (pt)"
                      : "Sheet height (pt)"
                }
              >
                <input
                  type="number"
                  min={1}
                  max={key === "copies" ? 100 : 14400}
                  value={config[key]}
                  onChange={(e) => update({ [key]: Number(e.target.value) })}
                />
              </Control>
            ))}
          </div>
          <Control label="Source pages">
            <select
              value={config.sourcePage}
              onChange={(e) => update({ sourcePage: e.target.value })}
            >
              <option value="all">All logical pages</option>
              {project.pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Control>
          <Control label="Grid layout">
            <select
              value={config.gridMode}
              onChange={(e) =>
                update({ gridMode: e.target.value as Imposition["gridMode"] })
              }
            >
              <option value="exact">Exact rows and columns</option>
              <option value="auto">Automatically fit copies</option>
            </select>
          </Control>
          {config.gridMode === "exact" && (
            <div className="control-grid">
              {(["columns", "rows"] as const).map((key) => (
                <Control
                  key={key}
                  label={key === "columns" ? "Columns" : "Rows"}
                >
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={config[key]}
                    onChange={(e) => update({ [key]: Number(e.target.value) })}
                  />
                </Control>
              ))}
            </div>
          )}
          <Control label="Item rotation">
            <select
              value={
                config.rotation ?? (config.rotate ? "quarter-turn" : "none")
              }
              onChange={(e) =>
                update({ rotation: e.target.value as Imposition["rotation"] })
              }
            >
              <option value="none">Keep orientation</option>
              <option value="quarter-turn">Rotate 90°</option>
              <option value="auto">Rotate automatically to fit</option>
            </select>
          </Control>
          <InsetsControl
            label="Sheet margins"
            value={config.margins ?? config.margin}
            onChange={(v) =>
              typeof v === "number"
                ? update({ margin: v, margins: undefined })
                : update({ margins: v })
            }
          />
          <Control label="Copy spacing">
            <select
              value={config.spacing}
              onChange={(e) =>
                update({ spacing: e.target.value as Imposition["spacing"] })
              }
            >
              <option value="fixed">Fixed horizontal and vertical gaps</option>
              <option value="even">Evenly distribute space</option>
            </select>
          </Control>
          <div className="control-grid">
            {(["gapX", "gapY"] as const).map((key) => (
              <Control
                key={key}
                label={
                  key === "gapX" ? "Horizontal gap (pt)" : "Vertical gap (pt)"
                }
              >
                <input
                  type="number"
                  min={0}
                  disabled={config.spacing === "even"}
                  value={config[key] ?? config.gap}
                  onChange={(e) => update({ [key]: Number(e.target.value) })}
                />
              </Control>
            ))}
          </div>
          <div className="control-grid">
            {(["alignX", "alignY"] as const).map((key) => (
              <Control
                key={key}
                label={
                  key === "alignX"
                    ? "Horizontal alignment"
                    : "Vertical alignment"
                }
              >
                <select
                  value={config[key]}
                  onChange={(e) => update({ [key]: e.target.value })}
                >
                  <option value="start">
                    {key === "alignX" ? "Left" : "Top"}
                  </option>
                  <option value="center">Center</option>
                  <option value="end">
                    {key === "alignX" ? "Right" : "Bottom"}
                  </option>
                </select>
              </Control>
            ))}
          </div>
          <Control label="Partial sheets">
            <select
              value={config.partialRow}
              onChange={(e) =>
                update({
                  partialRow: e.target.value as Imposition["partialRow"],
                })
              }
            >
              <option value="grid">Retain full grid positions</option>
              <option value="align">Align occupied rows and columns</option>
            </select>
          </Control>
          <Control label="Back-side placement">
            <select
              value={config.duplex}
              onChange={(e) =>
                update({ duplex: e.target.value as Imposition["duplex"] })
              }
            >
              <option value="mirror-columns">
                Mirror columns within the grid
              </option>
              <option value="mirror-sheet">
                Mirror positions across the sheet
              </option>
              <option value="none">Use front-side positions</option>
            </select>
          </Control>
          <Toggle
            label="Use page bleed for each copy"
            value={config.itemBleed === undefined}
            onChange={(inherit) =>
              update({
                itemBleed: inherit
                  ? undefined
                  : project.pages[0].production.bleed,
              })
            }
          />
          {config.itemBleed !== undefined && (
            <InsetsControl
              label="Item bleed"
              max={144}
              value={config.itemBleed}
              onChange={(itemBleed) => update({ itemBleed })}
            />
          )}
          <Toggle
            label="Crop marks for each copy"
            value={config.cropMarks.enabled}
            onChange={(enabled) =>
              update({ cropMarks: { ...config.cropMarks, enabled } })
            }
          />
          {config.cropMarks.enabled && (
            <div className="control-grid">
              {(["length", "offset", "strokeWidth"] as const).map((key) => (
                <Control
                  key={key}
                  label={
                    key === "length"
                      ? "Mark length (pt)"
                      : key === "offset"
                        ? "Mark offset (pt)"
                        : "Mark stroke (pt)"
                  }
                >
                  <input
                    type="number"
                    min={key === "offset" ? 0 : key === "strokeWidth" ? 0.1 : 1}
                    max={key === "strokeWidth" ? 2 : 144}
                    step={0.1}
                    value={config.cropMarks[key]}
                    onChange={(e) =>
                      update({
                        cropMarks: {
                          ...config.cropMarks,
                          [key]: Number(e.target.value),
                        },
                      })
                    }
                  />
                </Control>
              ))}
            </div>
          )}
          {proof.loading ? (
            <p role="status">Loading document layout…</p>
          ) : proof.error ? (
            <p role="alert" className="error-text">
              {proof.error}
            </p>
          ) : (
            plan && (
              <div className="sheet-summary" role="status">
                <strong>
                  {plan.sheets.length} sheets · {plan.copies} copies of each
                  selected page
                </strong>
                <p>
                  {plan.sheets[0].columns} columns × {plan.sheets[0].rows} rows
                  · {plan.rotation}° rotation · {plan.capacity} copies per sheet
                </p>
                <small>
                  The live proof shows one product. View or download this output
                  to inspect the imposed sheet and crop marks.
                </small>
                {plan.materialCost && (
                  <p>
                    {plan.physicalSheets} physical sheets · material estimate{" "}
                    {plan.materialCost.amount.toFixed(2)}{" "}
                    {plan.materialCost.currency}
                  </p>
                )}
              </div>
            )
          )}
          <SheetStockEditor
            value={config.stock}
            onChange={(stock) => update({ stock })}
          />
        </>
      )}
    </>
  );
}
